import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { getAccessToken } from "../store/actions/auth";
import Loading from "../components/Loading";
import API_BASE_URL from "../config/baseUrl";

// Calendar Icon
const CalendarIcon = () => (
  <svg
    viewBox="64 64 896 896"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M880 184H712v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H384v-64c0-4.4-3.6-8-8-8h-56c-4.4 0-8 3.6-8 8v64H144c-17.7 0-32 14.3-32 32v664c0 17.7 14.3 32 32 32h736c17.7 0 32-14.3 32-32V216c0-17.7-14.3-32-32-32zm-40 656H184V460h656v380zM184 392V256h128v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h256v48c0 4.4 3.6 8 8 8h56c4.4 0 8-3.6 8-8v-48h128v136H184z"></path>
  </svg>
);

// Swap Right Icon
const SwapRightIcon = () => (
  <svg
    viewBox="0 0 1024 1024"
    focusable="false"
    width="1em"
    height="1em"
    fill="currentColor"
  >
    <path d="M873.1 596.2l-164-208A32 32 0 00684 376h-64.8c-6.7 0-10.4 7.7-6.3 13l144.3 183H152c-4.4 0-8 3.6-8 8v60c0 4.4 3.6 8 8 8h695.9c26.8 0 41.7-30.8 25.2-51.8z"></path>
  </svg>
);

// Fetch statement/report data
const fetchStatementData = async function (detailType, fromDate, toDate) {
  try {
    const token = getAccessToken();

    if (!token) {
      return { success: false, error: "No access token found" };
    }

    const response = await axios.post(
      API_BASE_URL + "/ant-pro/get-accstt-chpdtl",
      {
        detailType: detailType,
        fromDate: fromDate,
        toDate: toDate,
      },
      { headers: { Authorization: "Bearer " + token } },
    );

    const data = response.data;

    if (data.status === false) {
      return { success: false, error: data.message || "Failed to fetch data" };
    }

    return { success: true, data: data.data || [] };
  } catch (err) {
    const errorMessage =
      err.response?.data?.message || "Failed to fetch statement data";
    return { success: false, error: errorMessage };
  }
};

// Get today's date in YYYY-MM-DD format
const getTodayDate = function () {
  const today = new Date();
  const year = today.getFullYear();
  const month = String(today.getMonth() + 1).padStart(2, "0");
  const day = String(today.getDate()).padStart(2, "0");
  return year + "-" + month + "-" + day;
};

// Get date 30 days ago in YYYY-MM-DD format
const getThirtyDaysAgoDate = function () {
  const date = new Date();
  date.setDate(date.getDate() - 30);
  const year = date.getFullYear();
  const month = String(date.getMonth() + 1).padStart(2, "0");
  const day = String(date.getDate()).padStart(2, "0");
  return year + "-" + month + "-" + day;
};

const StatementReportPage = () => {
  const navigate = useNavigate();

  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [data, setData] = useState([]);
  const [activeTab, setActiveTab] = useState("ALL");
  const [fromDate, setFromDate] = useState(getThirtyDaysAgoDate());
  const [toDate, setToDate] = useState(getTodayDate());

  // Fetch data
  const loadData = async function () {
    setLoading(true);
    setError(null);

    const result = await fetchStatementData(activeTab, fromDate, toDate);

    if (result.success) {
      setData(result.data);
    } else {
      setError(result.error);
      setData([]);
    }

    setLoading(false);
  };

  useEffect(
    function () {
      loadData();
    },
    [activeTab],
  );

  return (
    <div className="w-full">
      <div className="flex justify-center">
        <div className="w-full max-w-7xl px-2">
          <div className="bg-white rounded shadow border border-gray-200">
            {/* Header */}
            <div className="bg-[#0F2327] px-3 sm:px-5 py-3 flex items-center justify-between flex-wrap gap-2">
              <span className="text-base sm:text-lg md:text-xl text-white font-light capitalize truncate flex-1">
                List Of All Transactions ({data.length})
              </span>
              <button
                onClick={function () {
                  navigate(-1);
                }}
                className="bg-blue-500 text-white px-3 sm:px-4 py-1 hover:bg-blue-600 text-sm sm:text-base whitespace-nowrap"
                style={{ borderRadius: 0 }}
              >
                Back
              </button>
            </div>

            {/* Filters and Tabs */}
            <div className="flex flex-col lg:flex-row lg:justify-between lg:items-center w-full py-2 px-2 gap-3">
              {/* Date Range Picker */}
              <div className="w-full lg:w-auto">
                <div
                  className="flex items-center border border-gray-300 bg-white w-full lg:w-auto"
                  style={{ maxWidth: "100%", borderRadius: 0 }}
                >
                  <div className="flex-1 px-2 py-2">
                    <input
                      type="text"
                      readOnly
                      placeholder="Start date"
                      value={fromDate}
                      className="w-full text-xs sm:text-sm focus:outline-none bg-transparent"
                    />
                  </div>
                  <div className="px-2 text-gray-400 flex-shrink-0">
                    <SwapRightIcon />
                  </div>
                  <div className="flex-1 px-2 py-2">
                    <input
                      type="text"
                      readOnly
                      placeholder="End date"
                      value={toDate}
                      className="w-full text-xs sm:text-sm focus:outline-none bg-transparent"
                    />
                  </div>
                  <div className="px-2 text-gray-400 flex-shrink-0">
                    <CalendarIcon />
                  </div>
                </div>
              </div>

              {/* Tabs */}
              <div className="text-white text-sm sm:text-base md:text-lg font-bold flex items-center justify-start lg:justify-end">
                <div className="flex justify-center flex-nowrap">
                  <div
                    onClick={function () {
                      setActiveTab("ALL");
                    }}
                    className={
                      "px-2 sm:px-3 py-2 cursor-pointer text-xs sm:text-sm md:text-base " +
                      (activeTab === "ALL" ? "bg-gray-800" : "bg-blue-500")
                    }
                  >
                    All
                  </div>
                  <div
                    onClick={function () {
                      setActiveTab("PNL");
                    }}
                    className={
                      "px-2 sm:px-3 py-2 cursor-pointer text-xs sm:text-sm md:text-base " +
                      (activeTab === "PNL" ? "bg-gray-800" : "bg-blue-500")
                    }
                  >
                    P&amp;L
                  </div>
                  <div
                    onClick={function () {
                      setActiveTab("ACCOUNT");
                    }}
                    className={
                      "px-2 sm:px-3 py-2 cursor-pointer text-xs sm:text-sm md:text-base " +
                      (activeTab === "ACCOUNT" ? "bg-gray-800" : "bg-blue-500")
                    }
                  >
                    Account
                  </div>
                </div>
              </div>
            </div>

            {/* Table */}
            <div className="overflow-x-auto">
              {loading ? (
                <div className="flex justify-center py-8">
                  <Loading />
                </div>
              ) : error ? (
                <div className="p-4 text-red-500 text-center text-sm sm:text-base">
                  {error}
                </div>
              ) : (
                <table className="w-full min-w-full border-collapse text-xs sm:text-sm border border-gray-300">
                  <thead>
                    <tr className="bg-gray-100">
                      <th className="p-2 border border-gray-300 text-left font-medium whitespace-nowrap">
                        Date
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium">
                        Description
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium whitespace-nowrap">
                        CR
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium whitespace-nowrap">
                        DR
                      </th>
                      <th className="p-2 border border-gray-300 text-left font-medium whitespace-nowrap">
                        Balance
                      </th>
                    </tr>
                  </thead>
                  <tbody>
                    {data.length === 0 ? (
                      <tr>
                        <td
                          colSpan="5"
                          className="p-8 text-center text-gray-500 bg-gray-50"
                        >
                          No data
                        </td>
                      </tr>
                    ) : (
                      data.map(function (item, index) {
                        const rowBgClass = index % 2 === 0 ? "" : "bg-gray-50";

                        return (
                          <tr key={index} className={rowBgClass}>
                            <td className="p-2 border border-gray-300">
                              <span className="whitespace-nowrap px-1 sm:px-2 block">
                                {item.date || "-"}
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              <span className="px-1 sm:px-2 block break-words">
                                {item.description || "-"}
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              <span
                                className={
                                  item.credit > 0
                                    ? "text-green-600 font-semibold"
                                    : "text-black"
                                }
                              >
                                {item.credit || 0}
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              <span
                                className={
                                  item.debit > 0
                                    ? "text-red-600 font-semibold"
                                    : "text-black"
                                }
                              >
                                {item.debit || 0}
                              </span>
                            </td>
                            <td className="p-2 border border-gray-300">
                              <span
                                className={
                                  item.closing >= 0
                                    ? "text-green-600 font-semibold"
                                    : "text-red-600 font-semibold"
                                }
                              >
                                {item.closing || 0}
                              </span>
                            </td>
                          </tr>
                        );
                      })
                    )}
                  </tbody>
                </table>
              )}
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default StatementReportPage;
